import { useEffect, useMemo, useState } from "react";
import {
  Button,
  ButtonBase,
  Card,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon, Refresh as RefreshIcon } from "components/icons";
import { useTheme } from "@mui/material/styles";
import { CategoryScale, Chart as ChartJS, Filler, LinearScale, LineElement, PointElement, Tooltip } from "chart.js";
import { Line } from "react-chartjs-2";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  ROLE_ACCOUNT,
  ROLE_ADMIN,
  ROLE_BDM,
  ROLE_CLIENT,
  ROLE_EMPLOYEE_MANAGER,
  ROLE_EMPLOYEE_MEMBER,
  ROLE_MASTER_DATA_ADMIN,
  roleLabel,
} from "AppConstants";
import { getFeatureCatalog } from "analytics/featureCatalog";
import { ALL_ROLES_FILTER, everyDay, periodEnding, rankFeatures } from "analytics/usageSummary";
import { loadUsageSummary } from "app/services/analytics.service";
import { handleAxiosError } from "app/services/app.error.handler";
import { FONT_BODY } from "assets/theme/tokens/fonts";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip);

const PERIODS = [7, 30, 90];
const DEFAULT_PERIOD = 30;

const ROLE_OPTIONS = [
  { value: ALL_ROLES_FILTER, label: "All roles" },
  { value: ROLE_ADMIN, label: "Admin" },
  { value: ROLE_EMPLOYEE_MANAGER, label: "Manager" },
  { value: ROLE_EMPLOYEE_MEMBER, label: "Member" },
  { value: ROLE_BDM, label: roleLabel(ROLE_BDM) },
  { value: ROLE_CLIENT, label: "Client" },
  { value: ROLE_ACCOUNT, label: "Account" },
  { value: ROLE_MASTER_DATA_ADMIN, label: "Master data admin" },
];

// The chart draws on a canvas, which cannot read CSS variables, so it takes the theme's colour tokens
// directly. The fallbacks cover renders without the app's ThemeProvider (the component tests).
const chartColors = (theme) => {
  const tokens = theme.tokens ?? {};
  return {
    series: tokens.accent ?? theme.palette.primary.main,
    grid: tokens.border ?? theme.palette.divider,
    text: tokens["text-2"] ?? theme.palette.text.secondary,
    surface: tokens.surface ?? theme.palette.background.paper,
    tooltip: tokens.ink ?? theme.palette.grey[900],
  };
};

const formatCount = (n) => n.toLocaleString("en-IN");
const formatAdoption = (share) => (share == null ? "–" : `${Math.round(share * 100)}%`);
const formatDuration = (ms) => {
  if (ms == null) return "–";
  const seconds = Math.round(ms / 1000);
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`;
};
const formatTime = (date) =>
  date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
const formatDay = (day) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });

// Draws a hairline at the hovered day so the reader aims at a date, not at the 2px line.
const crosshairPlugin = (color) => ({
  id: "usageCrosshair",
  afterDatasetsDraw(chart) {
    const active = chart.tooltip?.getActiveElements?.() ?? [];
    if (active.length === 0) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(active[0].element.x, chartArea.top);
    ctx.lineTo(active[0].element.x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
});

// A segmented control matching the theme's tabs: one surface track, the chosen period lifted onto
// a pill in the accent colour. CSS variables, so it follows the base colour and light/dark mode.
const PERIOD_GROUP_SX = {
  height: 40,
  padding: "4px",
  gap: "4px",
  borderRadius: "var(--radius)",
  backgroundColor: "var(--surface-2)",
  border: "1px solid var(--border)",
  "& .MuiToggleButtonGroup-grouped": {
    border: 0,
    borderRadius: "var(--radius-sm) !important",
    margin: 0,
    px: 2,
    textTransform: "none",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: "var(--text-2)",
    transition: "background-color 150ms, color 150ms, box-shadow 150ms",
    "&:hover": { backgroundColor: "var(--accent-soft)", color: "var(--text)" },
    "&.Mui-selected": {
      backgroundColor: "var(--surface)",
      color: "var(--accent)",
      fontWeight: 600,
      boxShadow: "var(--shadow-1)",
    },
    "&.Mui-selected:hover": { backgroundColor: "var(--surface)" },
    "&.Mui-focusVisible": { outline: "2px solid var(--focus)", outlineOffset: 1 },
  },
};

// Disclosure toggles are ButtonBase, not Button: the app paints every Button as a filled accent
// button (App.css), which suits actions, not a "show more" link or a section heading.
const DISCLOSURE_SX = {
  gap: 0.5,
  py: 0.5,
  borderRadius: "var(--radius-sm)",
  "&.Mui-focusVisible": { outline: "2px solid var(--focus)", outlineOffset: 2 },
};
const SHOW_MORE_SX = {
  ...DISCLOSURE_SX,
  fontFamily: "var(--font-body)",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "var(--accent)",
  "&:hover": { color: "var(--accent-hover)" },
};
// Set like the theme's h6, so it reads as the same kind of heading as "People active each day".
const SECTION_HEADING_SX = {
  ...DISCLOSURE_SX,
  fontFamily: "var(--font-display)",
  fontSize: "1rem",
  fontWeight: 600,
  letterSpacing: "-0.01em",
  color: "var(--text)",
};

// A chevron that turns over when its section is open, so the toggle shows which way it will go.
const Chevron = ({ open }) => (
  <ExpandMoreIcon
    sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms" }}
  />
);

const StatTile = ({ label, value }) => (
  <MDBox>
    <MDTypography variant="body2" color="text">
      {label}
    </MDTypography>
    <MDTypography variant="h4" fontWeight="medium">
      {value}
    </MDTypography>
  </MDBox>
);

/**
 * ADMIN-only Usage Analytics (HRMS-322): which features are used most and least, by whom, and how
 * many people are active each day. Features nobody used stay listed, at zero.
 *
 * @since NEXT_VERSION
 */
export const UsageAnalyticsView = () => {
  const theme = useTheme();
  const colors = chartColors(theme);
  const series = colors.series;
  const gridColor = colors.grid;

  const [periodDays, setPeriodDays] = useState(DEFAULT_PERIOD);
  const [role, setRole] = useState(ALL_ROLES_FILTER);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDailyTable, setShowDailyTable] = useState(false);
  const [showFeatures, setShowFeatures] = useState(true);
  const [refreshes, setRefreshes] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);

  // Worked out again on every refresh, so a screen left open past midnight picks up the new day.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const period = useMemo(() => periodEnding(periodDays), [periodDays, refreshes]);

  useEffect(() => {
    let current = true;
    setLoading(true);
    loadUsageSummary(period.from, period.to)
      .then((response) => {
        if (current && response) {
          setSummary(response.data);
          setUpdatedAt(new Date());
        }
      })
      .catch(handleAxiosError)
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [period]);

  const rows = useMemo(() => rankFeatures(getFeatureCatalog(), summary, role), [summary, role]);
  const days = useMemo(
    () => everyDay(period.from, period.to, summary?.dailyActiveUsers),
    [period, summary]
  );
  const maxUses = Math.max(1, ...rows.map((row) => row.uses));
  const activePeople =
    role === ALL_ROLES_FILTER ? summary?.activeUsers ?? 0 : summary?.activeUsersByRole?.[role] ?? 0;
  const usedCount = rows.filter((row) => row.uses > 0).length;

  const chartData = {
    labels: days.map((entry) => formatDay(entry.day)),
    datasets: [
      {
        label: "People active",
        data: days.map((entry) => entry.users),
        borderColor: series,
        backgroundColor: `${series}1a`,
        borderWidth: 2,
        fill: true,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: series,
        pointHoverBorderColor: colors.surface,
        pointHoverBorderWidth: 2,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        backgroundColor: colors.tooltip,
        titleFont: { family: FONT_BODY },
        bodyFont: { family: FONT_BODY },
        callbacks: { label: (item) => `${formatCount(item.parsed.y)} people` },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { autoSkip: true, maxTicksLimit: 8, color: colors.text, font: { family: FONT_BODY } },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, color: colors.text, font: { family: FONT_BODY } },
        grid: { color: gridColor },
        border: { display: false },
      },
    },
  };

  return (
    <MDBox sx={{ mt: 5, px: 2 }}>
      <Card>
        <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
          <MDTypography variant="h6" color="white">
            Usage Analytics
          </MDTypography>
        </MDBox>

        <MDBox px={3} pt={3} display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={periodDays}
            onChange={(event, value) => value && setPeriodDays(value)}
            aria-label="Period"
            sx={PERIOD_GROUP_SX}
          >
            {PERIODS.map((days) => (
              <ToggleButton key={days} value={days}>
                {`Last ${days} days`}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="usage-role-label">Role</InputLabel>
            <Select
              labelId="usage-role-label"
              label="Role"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              sx={{ height: 40, borderRadius: "var(--radius)", fontSize: "0.875rem" }}
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDBox display="flex" alignItems="center" gap={1.5} ml="auto">
            {updatedAt && (
              <MDTypography variant="caption" color="text">
                Updated {formatTime(updatedAt)}
              </MDTypography>
            )}
            {/* Contained, since App.css fills every Button in the accent colour anyway. */}
            <Button
              variant="contained"
              color="info"
              startIcon={<RefreshIcon />}
              disabled={loading}
              onClick={() => setRefreshes((count) => count + 1)}
              sx={{ height: 40, borderRadius: "var(--radius)", textTransform: "none", fontWeight: 600 }}
            >
              Refresh
            </Button>
          </MDBox>
        </MDBox>

        <MDBox px={3} py={3} sx={{ opacity: loading && summary ? 0.5 : 1, transition: "opacity 150ms" }}>
          <MDBox display="flex" flexWrap="wrap" gap={6} mb={3}>
            <StatTile label="Active people" value={formatCount(activePeople)} />
            <StatTile label="Features used" value={`${usedCount} of ${rows.length}`} />
            <StatTile label="Never used" value={formatCount(rows.length - usedCount)} />
          </MDBox>

          <MDTypography variant="h6" mb={1}>
            People active each day
          </MDTypography>
          <MDBox sx={{ height: 240 }}>
            <Line data={chartData} options={chartOptions} plugins={[crosshairPlugin(gridColor)]} />
          </MDBox>
          {/* Each toggle sits in its own block: bare buttons are inline and ran together on one line. */}
          <MDBox mt={3}>
            <ButtonBase
              onClick={() => setShowDailyTable((shown) => !shown)}
              aria-expanded={showDailyTable}
              sx={SHOW_MORE_SX}
            >
              {showDailyTable ? "Hide daily numbers" : "Show daily numbers"}
              <Chevron open={showDailyTable} />
            </ButtonBase>
          </MDBox>
          <Collapse in={showDailyTable} unmountOnExit>
            <TableContainer sx={{ maxHeight: 280, mb: 2 }}>
              <Table size="small" aria-label="People active each day">
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell align="right">People active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {days.map((entry) => (
                    <TableRow key={entry.day}>
                      <TableCell>{formatDay(entry.day)}</TableCell>
                      <TableCell align="right">{formatCount(entry.users)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>

          <MDBox mt={3} mb={1}>
            <ButtonBase
              onClick={() => setShowFeatures((shown) => !shown)}
              aria-expanded={showFeatures}
              sx={SECTION_HEADING_SX}
            >
              Feature Adoption
              <Chevron open={showFeatures} />
            </ButtonBase>
          </MDBox>
          <Collapse in={showFeatures} unmountOnExit>
            <TableContainer>
              <Table size="small" aria-label="Feature adoption">
                <TableHead>
                  <TableRow>
                    <TableCell>Feature</TableCell>
                    <TableCell>Uses</TableCell>
                    <TableCell align="right">People</TableCell>
                    <TableCell align="right">Adoption</TableCell>
                    <TableCell align="right">Avg. time on page</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>
                        {row.label}
                        <MDTypography component="span" variant="caption" color="text" ml={1}>
                          {row.kind === "page" ? "Page" : "Action"}
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <MDBox
                            sx={{
                              height: 8,
                              width: `${(row.uses / maxUses) * 75}%`,
                              minWidth: row.uses > 0 ? 2 : 0,
                              backgroundColor: series,
                              borderRadius: "0 4px 4px 0",
                            }}
                          />
                          <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatCount(row.uses)}</span>
                        </MDBox>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCount(row.people)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatAdoption(row.adoption)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {row.kind === "page" ? formatDuration(row.avgDurationMs) : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </MDBox>
      </Card>
    </MDBox>
  );
};
